import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { mdiCheck, mdiContentCopy, mdiEmailOutline, mdiPlus } from '@mdi/js'
import { AppIcon } from '../ui/icon'
import { $api } from '../../api/queryClient'
import { Button } from '../ui/button'
import { useCan } from '../../contexts/PermissionsContext'
import type { ChannelEndpoint } from '../../api/schema'

/**
 * The site's own intake address, on the site page.
 *
 * This is where a manager actually works, and it is the address they are meant
 * to print in the AG convocation and pin on the notice board — so it has to be
 * one click away from the building, not buried behind the conversations queue.
 *
 * Opening one is idempotent per site: an address that changed because someone
 * clicked twice would be an address already printed on a wall. The address is
 * a public, right-less identifier — writing to it only drops a message in a
 * queue, and nothing auto-creates — which is precisely what makes it printable.
 */
export function AssetIntakeAddress({ org, assetUid }: { org: string; assetUid?: string }) {
    const { t } = useTranslation('conversations')
    const canRead = useCan('conversations.read')
    const canWrite = useCan('conversations.write')
    const queryClient = useQueryClient()
    const [copied, setCopied] = useState(false)

    // `asset_uid` narrows server-side: the site page must not have to page
    // through the whole portfolio's addresses to find its own (spec
    // 2026-09-09-13 § 2).
    const list = $api.useQuery(
        'get',
        '/api/v1/organizations/{orgCode}/channel-endpoints',
        { params: { path: { orgCode: org }, query: { asset_uid: assetUid } } },
        { enabled: Boolean(assetUid) && canRead },
    )

    const create = $api.useMutation('post', '/api/v1/organizations/{orgCode}/channel-endpoints', {
        onSuccess: () => {
            queryClient.invalidateQueries({
                predicate: (query) => {
                    const key = query.queryKey as unknown[]

                    return key.length >= 2 && key[0] === 'get'
                        && key[1] === '/api/v1/organizations/{orgCode}/channel-endpoints'
                },
            })
        },
    })

    if (!assetUid || !canRead) return null

    const endpoints = ((list.data as { data?: ChannelEndpoint[] } | undefined)?.data ?? []) as ChannelEndpoint[]
    const mine = endpoints.find(
        (endpoint) => (endpoint.asset as { uid?: string } | null | undefined)?.uid === assetUid,
    )

    const copy = async () => {
        if (!mine?.email_address) return
        try {
            await navigator.clipboard.writeText(mine.email_address)
            setCopied(true)
            setTimeout(() => setCopied(false), 1500)
        } catch {
            // A browser that refuses clipboard access is not an error worth a
            // banner: the address is on screen and selectable.
        }
    }

    return (
        <section className="space-y-2" data-testid="asset-intake-address">
            <h2 className="text-sm font-semibold text-foreground">{t('intake.title')}</h2>
            <div className="flex items-center gap-2 border rounded-lg px-3 py-2">
                <AppIcon path={mdiEmailOutline} size="sm" className="flex-shrink-0" />
                {mine ? (
                    <>
                        <span
                            className="text-xs md:text-sm font-mono text-foreground break-all leading-snug flex-1"
                            data-testid="asset-intake-address-value"
                        >
                            {mine.email_address}
                        </span>
                        <Button
                            variant="outline"
                            size="icon"
                            onClick={copy}
                            aria-label={t('intake.copy')}
                            title={t('intake.copy')}
                            data-testid="asset-intake-address-copy"
                        >
                            {copied ? <AppIcon path={mdiCheck} size="sm" /> : <AppIcon path={mdiContentCopy} size="sm" />}
                        </Button>
                    </>
                ) : (
                    <>
                        <span className="text-sm text-muted-foreground flex-1">{t('intake.none')}</span>
                        {canWrite && (
                            <Button
                                variant="outline"
                                size="sm"
                                disabled={create.isPending}
                                onClick={() => create.mutate({
                                    params: { path: { orgCode: org } },
                                    body: { channel: 'email', asset_uid: assetUid },
                                })}
                                data-testid="asset-intake-address-create"
                            >
                                <AppIcon path={mdiPlus} size="xs" className="md:mr-1" />
                                <span className="hidden md:inline">{t('intake.create')}</span>
                            </Button>
                        )}
                    </>
                )}
            </div>
            <p className="text-xs text-muted-foreground">{t('intake.hint')}</p>
        </section>
    )
}
